import { AxiosError } from 'axios';
import { StripeError } from '@stripe/stripe-js';
import { mapTimezoneToAPIName } from 'domains/calendars/dataNormalization';
import { scheduleConsultationFromSignupV2 } from 'domains/consultations/freeConsultationSchedule';
import {
  scheduleEvaluationFromSignupV3,
  ScheduleSignupEvaluationResponse,
  SignupSchedule,
} from 'domains/evaluations/evaluationSchedule';
import { queryClient } from 'domains/data';
import { TherapyTarget } from 'domains/signup/data/types';
import {
  CreateLeadResponseV3,
  createSalesforceLead,
  getSalesforceLead,
  updateSalesforceLead,
} from 'domains/signupv3/Leads/queries';
import { SignupLeadProgressV3, SignupLeadV3 } from 'domains/signupv3/Leads/types';
import { mergeCommunicationConcerns } from 'domains/signupv2/Mini-Screener/data/areaDetection';
import { CompletedSignupV3Data, IncompleteSignupV3Data } from 'domains/signupv3/Parent/signupMachineV3';
import { getGCLIDParameters } from 'domains/tracking/GCLIDtracking';
import { getUTMParameters } from 'domains/tracking/UTMTracking';
import { debounce, isUndefined } from 'lodash';
import { fromPromise } from 'xstate';
import { extractDate } from 'utils/date';
import { removeWhitespaces } from 'utils/phoneNumber';
import { StateAbbrev } from 'utils/states';
import { RelationshipEnum } from 'domains/client';
import { CompletedScheduleMachineContext, ScheduleMachineInput } from 'domains/signupv3/schedule/scheduleMachineV3';
import { EvaluationDuration } from 'domains/calendars/types';

export const loadLeadDataLogic = fromPromise<SignupLeadProgressV3 | null, { leadID: string }>(({ input }) => {
  return getSalesforceLead(input.leadID);
});

export const createLeadLogic = fromPromise<CreateLeadResponseV3, CompletedSignupV3Data>(({ input }) => {
  const UTMParams = getUTMParameters();
  const GCLIDParams = getGCLIDParameters();

  return createSalesforceLead({
    address: {
      state: input.state,
    },
    email: input.email,
    firstName: input.firstName,
    lastName: input.lastName,
    phone: input.phoneNumber,

    ...UTMParams,
    ...GCLIDParams,
  });
});

type ScheduleInput = Required<CompletedScheduleMachineContext & ScheduleMachineInput['machineConfig']>;

export const scheduleConsultationLogic = fromPromise<void, ScheduleInput>(({ input }) => {
  return scheduleConsultationFromSignupV2({
    consultationDate: input.consultationDate,
    consultationTimezone: input.consultationTimezone,
    schedulingId: input.schedulingID,
    appointmentType: 'initial-consult',
    calendarID: input.calendarID,
  });
});

export type ScheduleEvaluationErrorResponse = {
  originalError: AxiosError<{ error?: StripeError; message: string; status: string }>;
};

export const scheduleEvaluationLogic = fromPromise<void | ScheduleSignupEvaluationResponse, ScheduleInput>(
  ({ input }) => {
    const isAdult = input.target === 'adult';
    const clientFirstName =
      !isAdult && input.miniScreenerInfo?.clientFirstName ? input.miniScreenerInfo.clientFirstName : input.firstName;
    const clientLastName =
      !isAdult && input.miniScreenerInfo?.clientLastName ? input.miniScreenerInfo.clientLastName : input.lastName;

    const schedules: SignupSchedule[] = [
      {
        duration: EvaluationDuration.Single,
        timeSlot: input.evaluationDate.toISOString(),
        calendarID: (input.calendarID as number).toString(),
      },
    ];

    return scheduleEvaluationFromSignupV3({
      isResumeFlow: false,
      address: { state: input.state as StateAbbrev },
      amount: input.amount,
      schedules,
      schedulesTimezone: mapTimezoneToAPIName(input.evaluationTimezone),
      clientFirstName,
      clientLastName,
      contactFirstName: input.firstName,
      contactLastName: input.lastName,
      contactTimeZone: mapTimezoneToAPIName(input.evaluationTimezone),
      dob: extractDate(input.miniScreenerInfo.dob),
      email: input.email,
      isClientMinor: input.target === 'child',
      phone: { mobile: removeWhitespaces(input.phoneNumber) },
      relationshipToClient: input.miniScreenerInfo.relationshipToClient ?? RelationshipEnum.Client,
      leadID: input.leadID,
      stripeAccountID: input.stripeAccountId,
      stripePaymentMethodID: input.stripePaymentMethodId,
      skipStripePayment: false,
    });
  },
);

/** In ms */
const UPDATE_LEAD_DEBOUNCE_TIMER = 2000;

const getDesiredPaymentMethod = (useInsurance?: boolean): SignupLeadV3['desiredPaymentMethod'] | undefined => {
  if (isUndefined(useInsurance)) return undefined;

  return useInsurance ? 'Insurance' : 'Self-pay';
};

export const updateLead = debounce(
  (context: IncompleteSignupV3Data) => {
    const { miniScreenerInfo } = context;
    return queryClient.executeMutation({
      mutationFn: () =>
        updateSalesforceLead({
          id: context.leadID as string,
          schedulingId: context.schedulingID as string,
          address: context.state
            ? {
                state: context.state as string,
              }
            : undefined,
          firstName: context.firstName,
          lastName: context.lastName,
          email: context.email,

          phone: context.phoneNumber,
          serviceFor: context.target,

          communicationConcerns: miniScreenerInfo
            ? mergeCommunicationConcerns(miniScreenerInfo.concerns, context.target as TherapyTarget)
            : undefined,
          detectedAreas: miniScreenerInfo?.detectedAreas,

          desiredPaymentMethod: getDesiredPaymentMethod(context.insuranceInfo?.useInsurance),
          primaryLanguage: context.miniScreenerInfo?.primaryLanguage,
          relationshipToClient: context.miniScreenerInfo?.relationshipToClient,
          receivedSpeechTherapyBefore: context.miniScreenerInfo?.previousEvaluation,
          leadSource: 'Self-Referral - Web Inquiry',
        }),
      mutationKey: 'post-signup-lead',
    });
  },
  UPDATE_LEAD_DEBOUNCE_TIMER,
  {
    maxWait: 3000,
    leading: true,
  },
);
